import { useElements, useStripe } from '@stripe/react-stripe-js';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import { useMutation } from 'react-query';
import { SchemaPaymentsV2Service } from '@frontend/api-payments';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import { showConfirmPaymentErrors } from './stripe/stripe-errors';
import { useCreatePaymentIntentSync, UseCreatePaymentIntentSyncOptions } from './use-create-payment-intent-sync';
import { useValidateACHBalance, UseValidateACHBalanceOptions } from './use-validate-ach-balance';

export type UseConfirmStripePaymentProps = UseCreatePaymentIntentSyncOptions;

export const useConfirmStripePaymentViaPaymentMethod = (props: UseConfirmStripePaymentProps) => {
  const { t } = useTranslation('payments');
  const alerts = useAlert();
  const elements = useElements();

  const { locationId, paymentMethod } = props;

  const paymentMethodId = paymentMethod?.id;

  const {
    checkIsAchBalanceValidated,
    errorAlert,
    isElementsLoaded,
    isLocationLoaded,
    isPaymentMethodIdLoaded,
    isPaymentIdLoaded,
  } = useConfirmPaymentHelpers(props);

  const { createPaymentIntent } = useCreatePaymentIntentSync(props);

  const { mutateAsync: makePaymentMutation, isLoading: makingPayment } = useMutation({
    mutationFn: async () => {
      if (!(isElementsLoaded(elements) && isLocationLoaded(locationId) && isPaymentMethodIdLoaded(paymentMethodId))) {
        // alerts own errors
        return;
      }

      if (!(await checkIsAchBalanceValidated())) {
        // alerts own errors
        return;
      }

      const { paymentId } = await createPaymentIntent();

      if (!isPaymentIdLoaded(paymentId)) {
        // alerts own errors
        return;
      }

      try {
        await SchemaPaymentsV2Service.ConfirmPayment({
          locationId,
          paymentId,
          paymentMethodId,
        });

        alerts.success(t('Payment submitted successfully!'));
        return paymentId;
      } catch (e) {
        const err = e as { data?: { errorMessage?: string } };
        if (err?.data?.errorMessage) {
          errorAlert(t(`Error processing payment with Stripe. ${err.data.errorMessage}`));
        } else {
          errorAlert(t('Error processing payment with Stripe. Please try again.'));
        }
      }

      return;
    },
  });

  return {
    createPaymentIntent,
    makePayment: makePaymentMutation,
    makingPayment,
  };
};

export const useConfirmStripePaymentViaElements = (props: UseConfirmStripePaymentProps) => {
  const { t } = useTranslation('payments');
  const alerts = useAlert();
  const elements = useElements();
  const stripe = useStripe();

  const { locationId, amount } = props;

  const {
    checkIsAchBalanceValidated,
    errorAlert,
    isElementsLoaded,
    isLocationLoaded,
    isPaymentIdLoaded,
    isStripeLoaded,
  } = useConfirmPaymentHelpers(props);

  const { createPaymentIntent } = useCreatePaymentIntentSync(props);

  const submitElements = async (elements: StripeElements) => {
    let isSuccessful = false;
    try {
      elements.update({ amount });
      const { error } = await elements.submit();
      if (error) {
        errorAlert(t('There was an issue with the information provided. Please review and try again.'));
      }
      isSuccessful = !error;
    } catch {
      // stripe failed to communicate
      errorAlert(t('Error processing payment. Please try again.'));
    }
    return isSuccessful;
  };

  const stripeCreateConfirmationToken = async () => {
    if (!(isElementsLoaded(elements) && isStripeLoaded(stripe))) {
      return;
    }

    const { error, confirmationToken } = await stripe.createConfirmationToken({
      elements,
      params: {
        return_url: `${window.location.origin}/payments/invoices`,
      },
    });

    if (error) {
      // This point is only reached if there's an immediate error when
      // creating the ConfirmationToken. Show the error to your customer (for example, payment details incomplete)
      showConfirmPaymentErrors(error, errorAlert);
      return;
    }

    return confirmationToken;
  };

  const { mutateAsync: makePaymentMutation, isLoading: makingPayment } = useMutation({
    mutationFn: async () => {
      if (!(await checkIsAchBalanceValidated())) {
        // alerts own errors
        return;
      }

      const { paymentId } = await createPaymentIntent();

      if (!(isLocationLoaded(locationId) && isElementsLoaded(elements) && isPaymentIdLoaded(paymentId))) {
        // alerts own errors
        return;
      }

      if (!(await submitElements(elements))) {
        // alerts own errors
        return;
      }

      try {
        const confirmationToken = (await stripeCreateConfirmationToken())?.id;

        await SchemaPaymentsV2Service.ConfirmPayment({
          locationId,
          paymentId,
          confirmationToken: confirmationToken || 'token not found',
        });

        alerts.success(t('Payment submitted successfully!'));
        return paymentId;
      } catch (e) {
        const err = e as { data?: { errorMessage?: string } };
        if (err?.data?.errorMessage) {
          errorAlert(t(`Error processing payment with Stripe. ${err.data.errorMessage}`));
        } else {
          errorAlert(t('Error processing payment with Stripe. Please try again.'));
        }
      }

      return;
    },
  });

  return {
    createPaymentIntent,
    makePayment: makePaymentMutation,
    makingPayment,
  };
};

type UseConfirmPaymentHelperProps = UseValidateACHBalanceOptions;
const useConfirmPaymentHelpers = ({ amount, locationId, paymentMethod }: UseConfirmPaymentHelperProps) => {
  const alerts = useAlert();
  const { t } = useTranslation('payments');

  const errorAlert = (message: string) =>
    alerts.error({
      autoDismissAfter: 12000,
      message,
    });

  const { isACHBalanceValidated } = useValidateACHBalance({
    amount,
    locationId,
    paymentMethod,
  });

  const checkIsAchBalanceValidated = async () => {
    try {
      const balanceValidated = await isACHBalanceValidated();
      if (!balanceValidated) {
        errorAlert(t('Bank account does not have sufficient balance'));
        return false;
      }
    } catch (err) {
      console.error(err);
      errorAlert(t('Failed to check bank account balance'));
      return false;
    }

    return true;
  };

  const isStripeLoaded = (stripe: Stripe | null): stripe is Stripe => {
    if (!stripe) {
      errorAlert(t('Error creating confirmation. Stripe not loaded yet. Please try again.'));
      return false;
    }
    return true;
  };

  const isElementsLoaded = (elements: StripeElements | null): elements is StripeElements => {
    if (!elements) {
      errorAlert(t('Error creating confirmation. Stripe Elements not loaded yet. Please try again.'));
      return false;
    }
    return true;
  };

  const isLocationLoaded = (locationId: string | undefined): locationId is string => {
    if (!locationId) {
      errorAlert(t('Location ID is required.'));
      return false;
    }
    return true;
  };

  const isPaymentIdLoaded = (paymentId: string | undefined): paymentId is string => {
    if (!paymentId) {
      errorAlert(t('Failed to create payment intent. Please try again.'));
      return false;
    }
    return true;
  };

  const isPaymentMethodIdLoaded = (paymentMethodId: string | undefined): paymentMethodId is string => {
    if (!paymentMethodId) {
      errorAlert(t('Payment method ID is required.'));
      return false;
    }
    return true;
  };

  return {
    errorAlert,
    checkIsAchBalanceValidated,
    isPaymentIdLoaded,
    isPaymentMethodIdLoaded,
    isLocationLoaded,
    isElementsLoaded,
    isStripeLoaded,
  };
};
